<template>
  <div class="manage-modal">
    <HeaderModal
      :header-text="getUiText('app-manage-orgs-heading-1')"
      @close-modal="closeModal"
    />
  </div>
  <div class="org-modal-contain">
    <div class="first-row tw-flex tw-w-full tw-justify-between tw-items-center">
      <div class="search-box tw-flex tw-justify-center !tw-bg-[#fcfcfd]">
        <img src="/img/search_black_icon.svg" alt="search_icon" />
        <input
          v-model="search"
          type="text"
          :placeholder="getUiText('app-table-search-prompt-text-1')"
          class="tw-placeholder-[#B7B7B7] tw-font-[500] !tw-bg-[#fcfcfd]"
        />
      </div>
      <div class="tw-flex tw-items-center">
        <button
          class="button-show-archived tw-p-4 !tw-bg-[#fcfcfd] !tw-border !tw-border-solid !tw-border-[#EFF0F1]"
          @click="isShowArchived = !isShowArchived"
        >
          <span>{{ getUiText("app-manage-orgs-button-show-archived-1") }}</span>
          <ToggleSwitch
            :is-checked="isShowArchived"
            color="#2573E9"
            @click.stop
            @update:is-checked="isShowArchived = $event"
          />
        </button>
        <ButtonBulkActions
          :active="showBulkActions"
          :class="`!tw-border tw-border-1 !tw-border-solid !tw-border-[#EFF0F1] tw-mr-[15px]`"
          :button-class="`!tw-border !tw-border-solid !tw-border-[#EFF0F1]`"
          button-class-hover="!tw-bg-[#fcfcfd]"
          :selected-number="orgSelectedCount"
        />
        <ButtonFilter
          :active="false"
          :button-class="`!tw-border !tw-border-solid !tw-border-[#EFF0F1]`"
          button-class-hover="!tw-bg-[#fcfcfd]"
          :id-popover="'popover-btb-filter-manage-org-dropdown'"
          :is-margin-right="false"
        />
        <IconButton
          src="/img/plus_icon.svg"
          @click="
            () =>
              openCreateOrgModal(
                null,
                requestAccessDetailModalType.createRequest,
              )
          "
        />
      </div>
    </div>

    <div
      :class="`tw-w-full contain-table tw-mt-4 tw-rounded-md tw-max-h-[415px] tw-overflow-y-auto ${
        tableData.length === 0 ? 'tw-flex tw-items-center' : ''
      }`"
    >
      <ManageOrgTable
        :table-data="tableData"
        :table-fields="organizationManageTableFields"
        :current-org="currentOrg"
        @select:one_row="handleSelectOrg($event, false)"
        @select:all_row="handleSelectOrg($event, true)"
        @switch_org="switchOrg($event)"
        @leave_org="openModalLeaveOrg"
        @edit_request="
          (org) =>
            openCreateOrgModal(org, requestAccessDetailModalType.editRequest)
        "
        @create_org="
          (org) =>
            openCreateOrgModal(org, requestAccessDetailModalType.createOrg)
        "
        @archive_org="handleArchiveOrgRequest"
        @delete_org="handleDeleteOrgRequest"
      />
    </div>

    <div class="btn-access-request">
      <button class="button-accees" @click="gotToRequest">
        <span>{{
          getUiText("app-manage-orgs-request-org-access-text-1")
        }}</span>
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import store from "@/store";
import { computed, onMounted, reactive, ref, Ref, watch } from "vue";
import HeaderModal from "@/components/common/headerModal.vue";
import utilities, { getComponentUiText } from "@/utils/utilities";
import ManageOrgTable from "@/components/common/setting/manageOrgTable.vue";
import ButtonBulkActions from "../common/setting/buttonBulkActions.vue";
import ButtonFilter from "../common/setting/buttonFilter.vue";
import IconButton from "../common/iconButton.vue";
import router from "@/router";
import EventEmitter, { EmitterEvents } from "@/utils/eventEmitter";
import { IOrgDetails } from "@/interfaces/orgInterfaces";
import {
  deleteOrgRequest,
  leaveOrg,
  putVerificationRequestOrg,
} from "@/services/org";
import {
  localStorageBoolean,
  MODAL_NAME,
  requestAccessDetailModalType,
  successStatusCodes,
} from "@/utils/constants";
import { IToastMessage, ToastVariant } from "@/interfaces/common/IToastMessage";
import { showErrorAPI } from "@/services/apiRoutes";
import { Org } from "@/services/apiRoutes/routes";
import ToggleSwitch from "@/components/common/toggleSwitch.vue";
import { IModalMessage } from "@/interfaces/common/common";

export type orgItem = {
  id: number;
  name: string;
  icon: string;
  isHome: boolean;
  isShowMoreModal: boolean;
};

export type roleItem = {
  role: string;
  isStart: boolean;
};

export type orgItemTemp = {
  id: number;
  name: string;
  roles: Array<roleItem>;
  isShowMoreModal: boolean;
};

const props = defineProps({
  modelValue: {
    type: Boolean,
    required: false,
  },
  updateModal: {
    type: Boolean,
    required: false,
  },
  searchKey: {
    type: String,
    required: false,
    default: () => "",
  },
});

const isShowArchived = ref(false);
const orgListVerifyRef = ref<any[]>([]);
const orgList = computed(() => store.state.Org.orgsList);
const currentOrg = computed(() => store.state.Org.currentOrg);
const orgListVerify = computed(() => store.state.Org.orgListVerify);

const tableData = computed(() => {
  const orgListDefault = orgList.value.filter((org) => {
    org.isDefaultVerified = true;
    return org.name.toLowerCase().includes(search.value.toLowerCase());
  });
  const orgListUpdate = orgListVerifyRef.value.filter((org) => {
    org.name = org.name || org.nameShort;
    const isNamePassed =
      !org.name || org.name?.toLowerCase().includes(search.value.toLowerCase());
    const isArchivedPassed = !isShowArchived.value ? !org.isArchived : true;
    return isNamePassed && isArchivedPassed;
  });
  return orgListDefault.concat(orgListUpdate);
});

const emit = defineEmits(["closeModal", "createOrg", "leaveOrg"]);

onMounted(async () => {
  await store.dispatch("Org/fetchOrgListVerify");
});

watch(orgListVerify, () => {
  orgListVerifyRef.value = [...orgListVerify.value];
});

watch(isShowArchived, async () => {
  if (isShowArchived.value) {
    await store.dispatch("Org/fetchOrgListVerify", { includeArchived: true });
  }
});

watch(
  () => props.modelValue,
  (value) => {
    if (value) {
      fetchOrgs();
    }
  },
);

watch(
  () => props.updateModal,
  (value) => {
    if (value) {
      fetchOrgs(true);
    }
  },
);
watch(
  () => props.searchKey,
  () => {
    search.value = props.searchKey;
  },
);
const search = ref(props.searchKey);
const showBulkActions: Ref<boolean> = ref(false);
let orgSelected: orgItemTemp[] = reactive([]);
const orgSelectedCount = ref(0);

const defaultUiText = computed(() => store.state.LanguagePicker.defaultUiText);
const languageSelectedText = computed(
  () => store.state.LanguagePicker.languageSelectedText,
);
const getUiText = (id: string, isArray = false) => {
  return utilities.getUiText(
    id,
    languageSelectedText.value,
    defaultUiText.value,
    isArray,
  );
};

const organizationManageTableFields = [
  {
    key: "name",
    label: getUiText("app-component-custom-table-header-name-1"),
    thClass: ["header-org-name !tw-bg-[#fcfcfd]"],
  },
  {
    key: "userRoles",
    label: getUiText("app-manage-orgs-header-role-1"),
    tdClass: "tw-w-3/4",
    thClass: ["header-roles !tw-bg-[#fcfcfd]"],
  },
  {
    key: "status",
    label: getUiText("app-manage-orgs-header-status-1"),
    tdClass: "tw-w-1/4",
    thClass: ["header-roles !tw-bg-[#fcfcfd]"],
  },
  {
    key: "actionManage",
    label: "",
    thClass: ["header-actions !tw-bg-[#fcfcfd]"],
  },
];

const fetchOrgs = async (isUpdate = false) => {
  await store.dispatch("Org/fetchOrgList");
  if (isUpdate && orgList.value.length === 0) {
    closeModal();
  }
};

const closeModal = () => {
  emit("closeModal");
};

const openCreateOrgModal = (
  data: any,
  mode: keyof typeof requestAccessDetailModalType,
) => {
  // closeModal();
  emit("createOrg", { data, mode });
};

const handleSelectOrg = (orgs: orgItemTemp[], isSelectAll = false) => {
  if (isSelectAll) {
    orgSelected = Object.assign([], orgs);
  } else {
    orgs.forEach((org) => {
      const index = orgSelected.findIndex((item) => item.id === org.id);
      if (index !== -1) orgSelected.splice(index, 1);
      else orgSelected.push(org);
    });
  }

  if (orgSelected.length >= 1) showBulkActions.value = true;
  else showBulkActions.value = false;
  orgSelectedCount.value = orgSelected.length;
};

const switchOrg = (org) => {
  store.dispatch("Org/switchOrg", { orgId: org.id });
  closeModal();
};

const leaveOrgMembership = async (org: IOrgDetails) => {
  try {
    const res = await leaveOrg(org.id);

    if (res.status === successStatusCodes.OK) {
      store.dispatch("Org/fetchOrgList");
      showToastMessage(
        true,
        getUiText("app-manage-orgs-leave-org-toast-success-message-1"),
      );
    }
  } catch (error: any) {
    if (error && error.data) {
      showErrorAPI(error, Org.leaveOrg);
    }
  }
};

const gotToRequest = () => {
  emit("closeModal");
  router.push({
    name: "ManageAccess",
    query: { isOpenRequestModal: localStorageBoolean.true },
  });

  EventEmitter.emit(EmitterEvents.OPEN_REQUEST_MODAL);
};

const showToastMessage = (success = true, message: string) => {
  const emitData: IToastMessage = {
    variant: success ? ToastVariant.SUCCESS : ToastVariant.ERROR,
    message: message,
  };

  EventEmitter.emit(EmitterEvents.APP_LAYOUT_SHOW_TOAST_MESSAGE, emitData);
};

const handleDeleteOrgRequest = async (id: string) => {
  try {
    await deleteOrgRequest({ orgVerifyIds: [id] });
    orgListVerifyRef.value = orgListVerifyRef.value.filter(
      (item) => item.id !== id,
    );
  } catch (error) {
    showErrorAPI(error, Org.deleteOrgRequest);
  }
};

const handleArchiveOrgRequest = async (org: any, isArchived: boolean) => {
  try {
    await putVerificationRequestOrg(org.id, { isArchived });
    orgListVerifyRef.value.some((item) => {
      if (item.id === org.id) {
        org.isArchived = isArchived;
        return true;
      }
      return false;
    });
  } catch (error) {
    showErrorAPI(error, Org.editRequestVerification);
  }
};

const openModalLeaveOrg = (org: IOrgDetails) => {
  const emitData: IModalMessage = {
    component: MODAL_NAME.MODAL_RECOMMEN_ARE_YOU_SURE,
    properties: {
      value: {},
      title: getComponentUiText(
        "app-settings-team-members-profile-modal-remove-access-button-1",
      ),
      description: getComponentUiText(
        "app-settings-organisation-manage-access-remove-access-modal-helper-text-a-1",
      ),
      list: [],
      buttonText: getComponentUiText(
        "app-settings-team-members-profile-modal-remove-access-button-1",
      ),
      isShowBtnCancel: true,
      buttonRed: true,
      hiddenHelp: false,
    },
    events: {
      "update:data": () => leaveOrgMembership(org),
    },
  };
  EventEmitter.emit(EmitterEvents.OPEN_MODAL, emitData);
};
</script>

<style scoped lang="scss">
.manage-modal {
  border-bottom: 1px solid var(--ui-rule, #eff0f1);
}

.org-modal-contain {
  background: #fff;
  padding: 40px;
}

.search-box {
  border-radius: 6px;
  border: 1px solid var(--ui-rule, #eff0f1);
  padding: 11px 15px;
  color: white;
  font-size: 14px;
  font-weight: 500;
  max-width: 300px;
  width: 100%;
  outline: none;
  background: white;
  margin-right: 15px;
}

.search-box input {
  border: none;
  outline: none;
  width: 100%;
  margin-left: 10px;
}

.padding-action-box {
  padding: 8.5px 16px;
}

.margin-action-box {
  margin-left: 15px;
}

@media (max-width: 480px) {
  .search-box {
    display: none;
  }

  .header-container {
    padding: 20px 24px;
  }

  .org-modal-contain {
    padding: 40px 32px;
  }
}

.basic-text {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 600;
  font-size: 10px;
  line-height: 27px;
  letter-spacing: 0.1em;
}

.bulk-action-text {
  color: #b7b7b7;
  text-transform: uppercase;
}

.filter-action-text {
  color: #211f58;
  text-transform: uppercase;
}

.name-header-text {
  color: #211f58;
  text-transform: uppercase;
}

.role-header-text {
  color: #878787;
  text-transform: uppercase;
}

.bg-row-brown {
  background: #fcfcfd;
}

.role-box {
  border: 1px solid #eff0f1;
}

.org-name-text {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  color: #231f20;
}

.role-org-text {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 500;
  font-size: 11px;
  line-height: 130%;
  color: #231f20;
}

.btn-access-request {
  margin-top: 30px;
  align-items: flex-end;
  display: flex;
  flex-direction: column;
}

.button-accees {
  display: flex;
  padding: 8px 20px;
  background: #387dff;
  box-shadow: 0px 0px 15px rgba(0, 0, 0, 0.1);
  border-radius: 100px;
  border: none;
}

.btn-access-request .button-accees span {
  font-family: "Poppins";
  font-style: normal;
  font-weight: 500;
  font-size: 13px;
  line-height: 20px;
  display: flex;
  align-items: center;
  text-align: center;
  color: #ffffff;
}

.more-modal {
  position: absolute;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  box-shadow: 0 0 20px rgba(0, 0, 0, 0.2);
  border-radius: 6px;
  width: 178px;
  right: -68px;
}

.switch-text {
  padding: 15px;
  color: #211f58;
  border-bottom: 1px solid #eff0f1;
}

.leave-text {
  padding: 15px;
  color: #f7553c;
}

.decorator-modal-more {
  transform: rotate(45deg);
  width: 20px;
  height: 20px;
  background: white;
  position: absolute;
  top: -3px;
  right: 133px;
}

.current-text {
  color: #387dff;
  text-transform: uppercase;
  border: 1px solid #eff0f1;
  border-radius: 2px;
  padding: 0 4px;
}

.button-show-archived {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 15px;
  gap: 9px;
  background-color: #fff;
  height: 44px;
  border-radius: 6px;
  & > span {
    font-family: Poppins, sans-serif;
    text-transform: uppercase;
    font-weight: 600;
    font-size: 10px;
    color: #b7b7b7;
    letter-spacing: 1px;
  }
}

@media (max-height: 720px) {
  .contain-table {
    max-height: 50vh;

    @media (max-height: 720px) {
      max-height: 40vh;
    }
  }
}
</style>
